import { useCallback, useState } from "react";
import { Clock, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg, HTTP_CONFLICT } from "../lib/api";
import { fmtDate, shiftSpan } from "../lib/format";
import { Button } from "./ui/button";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "./ui/dialog";
import ConflictDialog from "./ConflictDialog";
import { ShiftFields } from "./ShiftFields";

export function EditTimeButton({ onClick, testid }) {
  return (
    <Button size="icon" variant="ghost" className="h-8 w-8 text-sky-300" onClick={onClick} data-testid={testid} aria-label="Modifica orario" title="Modifica orario">
      <Clock className="h-4 w-4" />
    </Button>
  );
}

function TimeDialog({ target, shift, setShift, busy, onSave, onClose }) {
  return (
    <Dialog open={!!target} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-popover max-w-md" data-testid="assignment-time-dialog">
        <DialogHeader><DialogTitle className="font-heading">Modifica orario</DialogTitle></DialogHeader>
        {target && <div className="text-sm text-muted-foreground">{target.resource_name} · {target.site_name} · {fmtDate(target.start_date)} → {fmtDate(target.end_date)}</div>}
        <ShiftFields value={shift} onChange={setShift} testid="assignment-time" />
        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={onClose} data-testid="assignment-time-cancel-btn">Annulla</Button>
          <Button onClick={onSave} disabled={busy || !shiftSpan(shift)} data-testid="assignment-time-save-btn">{busy && <Loader2 className="h-4 w-4 animate-spin mr-2" />}Salva orario</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function useAssignmentEdit(onDone) {
  const [target, setTarget] = useState(null);
  const [shift, setShift] = useState("");
  const [busy, setBusy] = useState(false);
  const [conflict, setConflict] = useState(null);
  const ask = useCallback((a) => {
    setTarget(a);
    setShift(a.shift || "");
  }, []);
  const save = async () => {
    setBusy(true);
    try {
      await api.put(`/assignments/${target.id}`, { shift });
      toast.success("✓ Orario aggiornato");
      setTarget(null);
      onDone();
    } catch (e) {
      if (e.response?.status === HTTP_CONFLICT) setConflict(e.response.data.detail.conflicts);
      else toast.error(`✕ ${errMsg(e)}`);
    } finally {
      setBusy(false);
    }
  };
  const dialog = (
    <>
      <TimeDialog target={target} shift={shift} setShift={setShift} busy={busy} onSave={save} onClose={() => setTarget(null)} />
      <ConflictDialog open={!!conflict} onOpenChange={() => setConflict(null)} items={conflict || []} />
    </>
  );
  return { ask, dialog };
}
